// src/pages/painel/componentes/AdicionarRefeicaoFavorito.js
import React, { useEffect, useMemo, useState } from "react";
import "./adicionarrefeicao-favorito.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";

const API_BASE = process.env.REACT_APP_API_BASE;
const ITENS_POR_PAGINA = 5;

const mapTipoRefeicaoToId = (tipo) => {
  switch (tipo) {
    case "cafe": return 1;
    case "almoco": return 2;
    case "jantar": return 3;
    case "lanche": return 4;
    default: return 1;
  }
};

const AdicionarRefeicaoFavorito = ({
  onClose,
  atualizarCalorias,
  onRefeicaoSalva,
  xpPorRefeicao = 0,
}) => {
  const [favoritos, setFavoritos] = useState([]);
  const [selecionado, setSelecionado] = useState(null);

  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);

  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");

  // 🆕 estados da remoção
  const [paraRemover, setParaRemover] = useState(null); // { id, fonte, titulo }
  const [removendo, setRemovendo] = useState(false);

  // ==========================================================
  // CARREGAR FAVORITOS
  // ==========================================================
  useEffect(() => {
    async function carregar() {
      try {
        const res = await fetch(
          `${API_BASE}/listar-favoritos-cardapio.php`,
          { credentials: "include" }
        );

        const data = await res.json();
        if (data.status === "sucesso") {
          setFavoritos(Array.isArray(data.favoritos) ? data.favoritos : []);
        }
      } catch {
        setErro("Erro ao carregar favoritos.");
      }
    }
    carregar();
  }, []);

  // ==========================================================
  // FILTRO POR BUSCA (tempo real)
  // ==========================================================
  const favoritosFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return favoritos;

    return favoritos.filter((f) =>
      f.titulo.toLowerCase().includes(termo)
    );
  }, [busca, favoritos]);

  // ==========================================================
  // PAGINAÇÃO (desativada quando busca ativa)
  // ==========================================================
  const totalPaginas = Math.ceil(
    favoritosFiltrados.length / ITENS_POR_PAGINA
  );

  const favoritosPaginados = useMemo(() => {
    if (busca.trim()) return favoritosFiltrados;

    const inicio = (pagina - 1) * ITENS_POR_PAGINA;
    return favoritosFiltrados.slice(
      inicio,
      inicio + ITENS_POR_PAGINA
    );
  }, [favoritosFiltrados, pagina, busca]);

  useEffect(() => {
    setPagina(1);
  }, [busca]);

  const selecionarFavorito = (fav) => {
    setSelecionado(fav);
    setErro("");
  };

  const salvarRefeicao = async () => {
    if (!selecionado) return;

    setCarregando(true);
    setErro("");

    try {
      const res = await fetch(`${API_BASE}/salvar-refeicao.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          refeicao_tipo_id: mapTipoRefeicaoToId(selecionado.tipo_refeicao),
          descricao_original: selecionado.itens,
          calorias_total: selecionado.calorias,
          proteinas_total: selecionado.proteinas,
          carboidratos_total: selecionado.carboidratos,
          gorduras_total: selecionado.gorduras,
          detalhes_itens_json: null,
          origem_calculo: "tabela",
          pago_com_moeda: false,
        }),
      });

      const data = await res.json();

      if (data.status === "sucesso") {
        if (typeof atualizarCalorias === "function") atualizarCalorias();
        onRefeicaoSalva?.(data.novas_conquistas || []);
        onClose();
      } else {
        setErro(data.mensagem || "Erro ao salvar refeição.");
      }
    } catch {
      setErro("Erro ao comunicar com o servidor.");
    }

    setCarregando(false);
  };

  // ==========================================================
  // 🆕 REMOÇÃO — abrir confirmação
  // ==========================================================
  const pedirConfirmacaoRemocao = (e, fav) => {
    e.stopPropagation(); // não seleciona o favorito ao clicar na lixeira
    setParaRemover({ id: fav.id, fonte: fav.fonte, titulo: fav.titulo });
  };

  const cancelarRemocao = () => {
    setParaRemover(null);
  };

  // ==========================================================
  // 🆕 REMOÇÃO — confirmar de verdade
  // ==========================================================
  const confirmarRemocao = async () => {
    if (!paraRemover) return;

    setRemovendo(true);

    try {
      const res = await fetch(`${API_BASE}/remover-favorito.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          id: paraRemover.id,
          fonte: paraRemover.fonte,
        }),
      });

      const data = await res.json();

      if (data.status === "sucesso") {
        // Remove da lista local, sem precisar recarregar tudo do servidor
        setFavoritos((prev) =>
          prev.filter(
            (f) => !(f.id === paraRemover.id && f.fonte === paraRemover.fonte)
          )
        );
        setParaRemover(null);
      } else {
        setErro(data.mensagem || "Erro ao remover favorito.");
        setParaRemover(null);
      }
    } catch {
      setErro("Erro ao comunicar com o servidor.");
      setParaRemover(null);
    }

    setRemovendo(false);
  };

  // ==========================================================
  // RENDER
  // ==========================================================
  return (
    <div className="favorito-container">

      {!selecionado && (
        <>
          {favoritos.length > 0 && (
            <>
              <h3>Escolha um favorito</h3>

              <input
                type="text"
                className="favorito-busca"
                placeholder="Buscar refeição..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </>
          )}

          {favoritosFiltrados.length === 0 && (
            <p className="favorito-vazio">
              Nenhum favorito encontrado.
            </p>
          )}

          <div className="favorito-lista">
            {favoritosPaginados.map((f) => (
              <button
                key={`${f.fonte}-${f.id}`}
                className="favorito-card"
                onClick={() => selecionarFavorito(f)}
              >
                {/* 🆕 lixeira, com stopPropagation pra não selecionar o card */}
                <button
                  type="button"
                  className="favorito-card-remover"
                  onClick={(e) => pedirConfirmacaoRemocao(e, f)}
                  aria-label="Remover dos favoritos"
                  title="Remover dos favoritos"
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                </button>

                <strong>{f.titulo}</strong>
                <p className="favorito-itens">
                    {f.itens}
                   </p>
                <span>{f.calorias} kcal</span>
              </button>
            ))}
          </div>

          {!busca.trim() && totalPaginas > 1 && (
            <div className="favorito-paginacao">
              <button
                disabled={pagina === 1}
                onClick={() => setPagina(p => p - 1)}
              >
                ‹
              </button>

              <span>{pagina} / {totalPaginas}</span>

              <button
                disabled={pagina === totalPaginas}
                onClick={() => setPagina(p => p + 1)}
              >
                ›
              </button>
            </div>
          )}
        </>
      )}

      {selecionado && (
        <>
          <div className="favorito-selecionado">
            <h3>{selecionado.titulo}</h3>
             <p>{selecionado.itens}</p>
          </div>

          {erro && <p className="erro-msg">{erro}</p>}

          <div className="resultado-container">
            <h3>Resultado nutricional</h3>

            <div className="resultado-kcal">
              <strong>{selecionado.calorias} kcal</strong>
            </div>

            <div className="macros-grid">
              <div className="macro">
                <span>Proteínas</span>
                <strong>{selecionado.proteinas} g</strong>
              </div>
              <div className="macro">
                <span>Carboidratos</span>
                <strong>{selecionado.carboidratos} g</strong>
              </div>
              <div className="macro">
                <span>Gorduras</span>
                <strong>{selecionado.gorduras} g</strong>
              </div>
            </div>

            <button
              className="btn-salvar"
              onClick={salvarRefeicao}
              disabled={carregando}
            >
              {carregando ? "Salvando..." : "Salvar refeição"}
            </button>

            {xpPorRefeicao > 0 && (
              <p className="label-xp-ganho">✨ +{xpPorRefeicao} XP</p>
            )}
          </div>
        </>
      )}

      {/* 🆕 MODAL DE CONFIRMAÇÃO DE REMOÇÃO */}
      {paraRemover && (
        <div className="favorito-confirmacao-fundo" onClick={cancelarRemocao}>
          <div
            className="favorito-confirmacao-caixa"
            onClick={(e) => e.stopPropagation()}
          >
            <p>
              Remover <strong>{paraRemover.titulo}</strong> dos favoritos?
            </p>

            <div className="favorito-confirmacao-botoes">
              <button
                type="button"
                className="favorito-confirmacao-cancelar"
                onClick={cancelarRemocao}
                disabled={removendo}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="favorito-confirmacao-remover"
                onClick={confirmarRemocao}
                disabled={removendo}
              >
                {removendo ? "Removendo..." : "Remover"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdicionarRefeicaoFavorito;